"use client"

import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { sendOtp, clearSendOtpError, clearSendOtpState } from "../../store/slices/sendOtpSlice"
import { verifyOtp, clearVerifyOtpError } from "../../store/slices/verifyOtpSlice"
import { generateRecaptchaToken } from "../../lib/utils/recaptcha"
import { registerWithPassword } from "../../store/slices/registerPasswordSlice"
import { checkInvitedUserExists, setInvitationToken, loginInvitedUser } from "../../store/slices/invitedUserSlice"
import { fetchUserProfile, clearUserProfile } from "../../store/slices/userSlice"
import { setCredentials } from "../../store/slices/authSlice"
import { fetchClientsByUserId, clearClients } from "../../store/slices/clientsSlice"
import "./InviteUser.css"
import OtpPopup from "../signup/OtpPopup"
import { getAuthMessage } from "../../lib/constants/authStatusCodes"
import {
  getPasswordEasyPatternError,
  getConfirmPasswordMismatchError,
  isNewPasswordSubmittable,
} from "../../lib/utils/passwordUtils"
import { PasswordFieldWithRequirements, PasswordFieldSimple } from "../common/PasswordField"
import { sendResetLink, clearResetState } from "../../store/slices/resetPasswordSlice"
import ResetLinkSuccessPopup from "../common/ResetLinkSuccessPopup/ResetLinkSuccessPopup"
import "../login/LoginPage.css"
import ForgotPasswordPopup from "../common/ForgotPasswordPopup/ForgotPasswordPopup"

/* Same background as Reset Password - exact same inline style */
const INV_WRAPPER_BG_STYLE = {
  backgroundImage: "url(/assets/illustrations/soho-image.png)",
  backgroundRepeat: "no-repeat",
  backgroundSize: "150% auto",
  backgroundPosition: "top 25px right -100px",
}

const InviteUser = () => {
  // POPUP STATES
  const [openPasswordPopup, setOpenPasswordPopup] = useState(false)
  const [openLoginPopup, setOpenLoginPopup] = useState(false)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)


  // OTP POPUP
  const [showPopup, setShowPopup] = useState(false)
  const [popupStatus, setPopupStatus] = useState("sent")

  const [otpSent, setOtpSent] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [otpVerified, setOtpVerified] = useState(false)
  const [enteredEmail, setEnteredEmail] = useState("")
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""])
  const otpRefs = useRef([])
  const [otpTimer, setOtpTimer] = useState(0) // seconds
  const [canResendOtp, setCanResendOtp] = useState(false)
  const [showOtpFields, setShowOtpFields] = useState(false)


  const location = useLocation()
  const navigate = useNavigate()
  const { token } = useParams()
  const dispatch = useDispatch()
  const [inviteCheckDone, setInviteCheckDone] = useState(false)
  const [isAuthInProgress, setIsAuthInProgress] = useState(false)
  const [popupMessage, setPopupMessage] = useState("")
  const [popupErrorTitle, setPopupErrorTitle] = useState("")



  const {
    userExists,
    userData,
    loading: checkingUser,
    error: checkError,
    token: storedToken,
    loginLoading,
    loginError,
    authSuccess,
    accessToken,
  } = useSelector((state) => state.invitedUser)

  const { loading, success, error, data: otpData } = useSelector((state) => state.sendOtp)
  const { loading: verifyLoading, isVerified, error: verifyError, data: verifyData } = useSelector((state) => state.verifyOtp)
  const resetError = useSelector((state) => state.resetPassword.error)

  const {
    loading: signupLoading,
    error: signupError,
    success: signupSuccess,
  } = useSelector((state) => state.registerPassword)

  // Password states
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")

  const [rememberMe, setRememberMe] = useState(false)

  const [showForgotPopup, setShowForgotPopup] = useState(false)
  const [forgotEmail, setForgotEmail] = useState("")

  const [showResetSuccessPopup, setShowResetSuccessPopup] = useState(false)
  const [resetLinkHeading, setResetLinkHeading] = useState("Password reset link sent")
  const [resetEmail, setResetEmail] = useState("")
  const [forgotLocalError, setForgotLocalError] = useState("")

  const allValid = isNewPasswordSubmittable(
    password,
    confirm,
    userData?.inviteeFullName || "",
  )

  const [fetchingProfile, setFetchingProfile] = useState(false)

  useEffect(() => {
    if (token) {
      dispatch(setInvitationToken(token))
      dispatch(checkInvitedUserExists(token))
      navigate("/invite-user", { replace: true })
    }
  }, [token, dispatch, navigate])

  useEffect(() => {
    if (
      !token &&
      storedToken &&
      userExists === null &&
      !checkingUser &&
      !inviteCheckDone
    ) {
      dispatch(checkInvitedUserExists(storedToken))
      setInviteCheckDone(true) // stop further calls
    }
  }, [storedToken, userExists, checkingUser, token, inviteCheckDone, dispatch])
  useEffect(() => {
    if (token) {
      setInviteCheckDone(false)
    }
  }, [token])


  useEffect(() => {
    if (
      userExists !== null &&
      !checkingUser &&
      userData &&
      !authSuccess &&          // ✅ STOP after login starts
      !isAuthInProgress        // ✅ STOP during login/signup
    ) {
      if (userData?.inviteeEmail) {
        setEnteredEmail(userData.inviteeEmail)
      }
      if (userExists) {
        setOpenLoginPopup(true)
      } else {
        setOpenPasswordPopup(true)
      }
    }
  }, [userExists, checkingUser, userData, authSuccess, isAuthInProgress])

  useEffect(() => {
    if (signupSuccess && !signupLoading && !signupError) {
      setIsAuthInProgress(true)
      dispatch(
        loginInvitedUser({
          email: enteredEmail,
          username: enteredEmail,
          password,
          token: storedToken,
          rememberMe,
        })
      )
    }
  }, [signupSuccess, signupLoading, signupError])


  useEffect(() => {
    if (authSuccess && accessToken) {
      dispatch(clearUserProfile())
      dispatch(clearClients())

      // Sync the token with auth slice so API requests work correctly
      dispatch(
        setCredentials({
          token: accessToken,
          user: { username: enteredEmail, email: enteredEmail },
        }),
      )

      setOpenLoginPopup(false)
      setOpenPasswordPopup(false)
      handleFetchProfileAfterLogin()
    }
  }, [authSuccess, accessToken, dispatch, enteredEmail])

  useEffect(() => {
    if (success && otpData) {
      setOtpSent(true)
      setShowOtpFields(true)
      setPopupStatus("sent")
      setPopupMessage(otpData?.message)
      setShowPopup(true)

      // ⏱ Start 60s timer
      setOtpTimer(60)
      setCanResendOtp(false)

      const interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            setCanResendOtp(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      setTimeout(() => {
        setShowPopup(false)
      }, 5000)

      return () => clearInterval(interval)
    }
  }, [success, otpData])

  // Backend can return 200 with success: false (e.g. RESEND_TOO_SOON) — use API code/message, no generic INVALID_OTP
  useEffect(() => {
    if (!loading && otpData && otpData.success === false) {
      const code = otpData.code
      const apiMessage = otpData.message
      const heading = apiMessage || (code ? getAuthMessage(code) : "Request failed")
      const description = code ? getAuthMessage(code) : apiMessage || "Please try again."
      setPopupStatus("error")
      setPopupErrorTitle(heading)
      setPopupMessage(description)
      setShowPopup(true)
      const t = setTimeout(() => setShowPopup(false), 5000)
      dispatch(clearSendOtpState())
      return () => clearTimeout(t)
    }
  }, [loading, otpData, dispatch])

  useEffect(() => {
    if (isVerified) {
      setOtpVerified(true)


      setTimeout(() => {
        setShowOtpFields(false)
      }, 1200)

      setPopupStatus("verified")
      setPopupMessage(verifyData?.message)
      setShowPopup(true)

      setTimeout(() => {
        setShowPopup(false)
      }, 5000)
    }
  }, [isVerified])


  useEffect(() => {
    if (!signupError) return
    setPopupStatus("error")
    const payload = typeof signupError === "object" ? signupError : { message: signupError }
    const code = payload.code
    const apiMessage = payload.message
    const title = apiMessage || (code ? getAuthMessage(code) : "Registration failed")
    const description = code ? getAuthMessage(code) : apiMessage || "Something went wrong. Please try again."
    setPopupErrorTitle(title)
    setPopupMessage(description)
    setShowPopup(true)
    const t = setTimeout(() => setShowPopup(false), 5000)
    return () => clearTimeout(t)
  }, [signupError])

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return // Only allow digits

    if (verifyError) {
      dispatch(clearVerifyOtpError())
    }

    const newDigits = [...otpDigits]
    newDigits[index] = value
    setOtpDigits(newDigits)

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleVerifyOtp = () => {
    const otpCode = otpDigits.join("")
    if (otpCode.length === 6) {
      dispatch(verifyOtp({ email: enteredEmail, otp: otpCode }))
        .unwrap()
        .then((data) => {
          setPopupStatus("verified")
          setPopupMessage(data?.message)
          setShowPopup(true)
          setTimeout(() => setShowPopup(false), 5000)
        })
        .catch((err) => {
          const code = err?.code
          const apiMessage = err?.message
          const heading = apiMessage || (code ? getAuthMessage(code) : "Verification failed")
          const description = code ? getAuthMessage(code) : apiMessage || "Please try again."
          setPopupStatus("error")
          setPopupErrorTitle(heading)
          setPopupMessage(description)
          setShowPopup(true)
          setTimeout(() => setShowPopup(false), 5000)
        })
    }
  }

  const handleResendOtp = async () => {
    dispatch(clearSendOtpError())

    try {
      // Generate captcha token (same as Signup)
      const captchaToken = await generateRecaptchaToken("sendotp")

      dispatch(
        sendOtp({
          email: enteredEmail,
          full_name: userData?.inviteeFullName || "Guest User",
          captchaToken, // REQUIRED
        }),
      )
    } catch (error) {
      console.error("Failed to generate reCAPTCHA token:", error)
      setShowPopup(true)
      setPopupStatus("error")
    }
  }

  const handleCreateAccount = () => {
    if (!otpVerified) {
      console.error(" ERROR: OTP not verified!")
      return
    }

    if (!allValid) {
      console.error("ERROR: Password validation failed!")
      return
    }

    if (!storedToken) {
      console.error("ERROR: No invitation token found!")
      console.error("DEBUG: Current Redux state token:", storedToken)
      return
    }

    generateRecaptchaToken("signup")
      .then((captchaToken) => {
        dispatch(
          registerWithPassword({
            email: enteredEmail,
            username: enteredEmail, // Use email as username
            password: password,
            fullName: userData?.inviteeFullName || "", // Use invitee full name from API
            token: storedToken, // Pass invitation token (will be sent as query param)
            captchaToken,
            captchaSiteKey: "RECAPTCHA_SITE_KEY",
          }),
        )
      })
      .catch((error) => {
        console.error("Failed to generate reCAPTCHA token:", error)
        setShowAlert(true)
        setTimeout(() => setShowAlert(false), 5000)
      })
  }

  const handleLogin = () => {
    if (password && enteredEmail && storedToken) {
      setIsAuthInProgress(true)
      dispatch(clearUserProfile())
      dispatch(clearClients())

      dispatch(
        loginInvitedUser({
          email: enteredEmail,
          username: enteredEmail,
          password,
          token: storedToken,
          rememberMe,
        })
      )
    }
  }
  useEffect(() => {
    if (authSuccess || loginError) {
      setIsAuthInProgress(false)
    }
  }, [authSuccess, loginError])

  const getArticle = (role) => {
    const formattedRole = formatRole(role)
    return /^[AEIOU]/i.test(formattedRole) ? "an" : "a"
  }



  const getLoginErrorMessage = () => {
    if (!loginError) return null

    const codeUpper = String(loginError).trim().toUpperCase()
    const isUserNotFound =
      codeUpper === "USER_NOT_FOUND" ||
      codeUpper === "EMAIL_NOT_FOUND" ||
      codeUpper.includes("USER_NOT_FOUND") ||
      codeUpper.includes("EMAIL_NOT_FOUND") ||
      codeUpper.includes("USER NOT FOUND") ||
      codeUpper.includes("NOT FOUND") ||
      codeUpper.includes("401")

    if (isUserNotFound) {
      return (
        <div className="inv-login-error-box">
          <p className="inv-login-error-title">Account Not Found</p>
          <p className="inv-login-error-text">
            The email <strong>{enteredEmail}</strong> doesn't have a SoHo account yet. Please{" "}
            <span
              className="inv-login-link"
              onClick={() => {
                setOpenLoginPopup(false)
                setOpenPasswordPopup(true)
              }}
            >
              sign up
            </span>{" "}
            to create your account.
          </p>
        </div>
      )
    }

    return <p className="inv-otp-error">{getAuthMessage(loginError)}</p>
  }
  const formatRole = (role) =>
    role
      ?.replace(/^ROLE_/, "")
      .toLowerCase()
      .replace(/_/g, " ")
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  const handleFetchProfileAfterLogin = async () => {
    setFetchingProfile(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 100))

      const userProfile = await dispatch(fetchUserProfile({ force: true })).unwrap()

      // Also fetch clients data if user profile has an ID
      if (userProfile?.id) {
        try {
          await dispatch(fetchClientsByUserId(userProfile.id)).unwrap()
        } catch (clientsErr) {
          console.error(" Failed to fetch clients after login:", clientsErr)
          // Continue anyway - clients might be fetched later
        }
      }
    } catch (error) {
      console.error("Failed to fetch user profile after login:", error)
    } finally {
      setFetchingProfile(false)
      setShowSuccessPopup(true)
    }
  }

  if (checkingUser) {
    return (
      <div className="inv-wrapper" style={INV_WRAPPER_BG_STYLE}>
        <div className="inv-card">
          <img src="/assets/brand/soho_logo_sfh.svg" alt="SoHo Logo" className="inv-header-logo" style={{ marginBottom: "32px" }} />
          <h2 className="inv-title">Verifying invitation...</h2>
          <p className="inv-text">Please wait while we check your invitation.</p>
        </div>
      </div>
    )
  }

  if (checkError) {
    return (
      <div className="inv-wrapper">
        <div className="inv-card">
          <img src="/assets/brand/soho_logo_new.svg" alt="Soho Logo" className="inv-header-logo" style={{ marginBottom: "32px" }} />
          <h2 className="inv-text">Link Expired</h2>
          {/* <p className="inv-text">{checkError}</p> */}
          <button className="inv-continue-btn" onClick={() => navigate("/login")}>
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* ===================== PASSWORD POPUP (SIGNUP) ===================== */}
      {!checkingUser && !openLoginPopup && !showSuccessPopup && !isAuthInProgress && userExists === false && (
        <div className="inv-wrapper" style={INV_WRAPPER_BG_STYLE}>
          <div className="inv-page-box">
            <div className="inv-form-logo-wrap">
              <img src="/assets/brand/soho_social.svg" alt="SoHo - SOCIAL FOR HOSPITALITY" className="inv-form-logo" />
            </div>
            <h2 className="inv-popup-title">Create your password</h2>
            <p className="inv-popup-sub">
              Set a password to join <strong>{userData?.client?.name || "Hyatt International"}</strong> on SoHo.
            </p>

            {showAlert && <div className="inv-alert-message">Failed to create account. Please try again.</div>}

            <label className="inv-input-label">Business Email</label>

            <div className="inv-input-wrapper">
              <input
                className="inv-input"
                placeholder="Enter your work/business email"
                value={enteredEmail}
                onChange={(e) => setEnteredEmail(e.target.value)}
                disabled={true}
              />

              <button
                className="inv-otp-btn"
                onClick={handleResendOtp}
                disabled={loading || !enteredEmail || (otpSent && !canResendOtp) || otpVerified}
              >
                {loading ? "Sending..." : otpSent ? "Resend OTP" : "Get OTP"}
              </button>
            </div>
            {otpSent && !canResendOtp && !error && !otpVerified && (
              <p className="otp-timer-text">
                Resend OTP in <span className="otp-timer">{otpTimer}s</span>
              </p>
            )}
            {error && <p className="inv-otp-error">{typeof error === "object" ? error?.message : error}</p>}

            <p className="inv-input-hint">
              We'll send a verification code to this email.
            </p>

            {showOtpFields && (

              <>
                <label className="inv-input-label">Enter Code</label>
                <div className="inv-otp-box-container">
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      className="inv-otp-box"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !digit && i > 0) {
                          otpRefs.current[i - 1]?.focus()
                        }
                      }}
                      disabled={otpVerified}
                    />
                  ))}
                  {otpVerified && (
                    <p className="inv-success-text">
                      <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M16.5 8.31039V9.00039C16.4991 10.6177 15.9754 12.1914 15.007 13.4868C14.0386 14.7821 12.6775 15.7297 11.1265 16.1883C9.57557 16.6469 7.91794 16.5918 6.40085 16.0313C4.88376 15.4708 3.58849 14.435 2.70822 13.0782C1.82795 11.7214 1.40984 10.1164 1.51626 8.50262C1.62267 6.88881 2.24791 5.35263 3.29871 4.12319C4.34951 2.89375 5.76959 2.03692 7.34714 1.6805C8.92469 1.32407 10.5752 1.48714 12.0525 2.14539"
                          stroke="#29A155"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.5 3L9 10.5075L6.75 8.2575"
                          stroke="#29A155"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Verified
                    </p>
                  )}
                  {!otpVerified && (
                    <button
                      className="inv-verify-btn"
                      onClick={handleVerifyOtp}
                      disabled={verifyLoading || otpDigits.join("").length !== 6}
                    >
                      {verifyLoading ? "Verifying..." : "Verify"}
                    </button>
                  )}
                </div>
                {verifyError && !otpVerified && <p className="inv-otp-error">{typeof verifyError === "object" ? verifyError?.message : verifyError}</p>}
              </>
            )}

            {!otpVerified && (
              <button className="inv-create-btn" disabled={true}>
                Create Account & Join
              </button>
            )}

            {otpVerified && !showOtpFields && (

              <>
                <PasswordFieldWithRequirements
                  className="rp-password-field"
                  id="inv-password"
                  name="password"
                  label="Create Password"
                  placeholder="Enter Password"
                  value={password}
                  onChange={setPassword}
                  fullName={userData?.inviteeFullName || ""}
                  error={getPasswordEasyPatternError(
                    password,
                    userData?.inviteeFullName || "",
                  )}
                />
                <PasswordFieldSimple
                  className="rp-password-field"
                  id="inv-confirm"
                  name="confirmPassword"
                  label="Confirm Password"
                  placeholder="Re-enter Password"
                  value={confirm}
                  onChange={setConfirm}
                  error={getConfirmPasswordMismatchError(password, confirm) || ""}
                />
                <button
                  className={`inv-create-btn ${allValid ? "inv-active" : ""}`}
                  onClick={handleCreateAccount}
                  disabled={!allValid || signupLoading}
                >
                  {signupLoading ? "Creating Account..." : "Create Account & Join"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===================== LOGIN POPUP ===================== */}
      {!checkingUser && openLoginPopup && !showSuccessPopup && (
        <div className="inv-wrapper" style={INV_WRAPPER_BG_STYLE}>
          <div className="inv-page-box">
            <h2 className="inv-popup-title">Log in to accept your invitation</h2>
            <p className="inv-popup-sub">
              Use your existing SoHo account to join <strong>{userData?.client?.name || "Hyatt International"}</strong>
            </p>
            <label className="inv-input-label">Business Email</label>
            <input
              value={enteredEmail}
              className="inv-input inv-email-input"
              disabled
            />
            <PasswordFieldSimple
              id="inv-login-password"
              name="password"
              label="Password"
              placeholder="Enter Password"
              value={password}
              onChange={setPassword}
              inputClassName="inv-password-field"
              error={loginError ? getAuthMessage(loginError) : ""}
            />

            <div className="rem-forg">
              <div className="rememberMe">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="remember-me">
                  Remember me
                </label>
              </div>

              <div
                className="forgotPass"
                onClick={() => {
                  setForgotEmail(enteredEmail)
                  setShowForgotPopup(true)
                }}
              >
                Forgot password?
              </div>
            </div>

            <button
              className={`inv-create-btn ${password ? "inv-active" : ""}`}
              onClick={handleLogin}
              disabled={!password || loginLoading}
            >
              {loginLoading ? "Logging in..." : "Login & Join"}
            </button>
          </div>
        </div>
      )}

      {showSuccessPopup && (
        <div className="inv-wrapper" style={INV_WRAPPER_BG_STYLE}>
          <div className="inv-page-box">
            <div className="inv-form-logo-wrap">
              <img src="/assets/brand/soho_social.svg" alt="SoHo - SOCIAL FOR HOSPITALITY" className="inv-form-logo" />
            </div>
            <h2 className="inv-popup-title">Welcome to the team!</h2>
            <p className="inv-popup-sub">
              You've successfully joined
              <br />
              <strong>{userData?.client?.name}</strong>
              <br />
              as {getArticle(userData?.role)} {formatRole(userData?.role)}.
            </p>
            <button className="inv-create-btn inv-active" onClick={() => navigate("/calendar")}>
              {fetchingProfile ? "Loading..." : "Continue to Calendar"}
            </button>
          </div>
        </div>
      )}

      <ForgotPasswordPopup
        isOpen={showForgotPopup}
        defaultEmail={enteredEmail}
        onClose={() => {
          setShowForgotPopup(false)
        }}
        onSuccess={({ heading, email }) => {
          setResetLinkHeading(heading)
          setResetEmail(email)
          setShowForgotPopup(false)
          setShowResetSuccessPopup(true)
        }}
      />

      <ResetLinkSuccessPopup
        isVisible={showResetSuccessPopup}
        onClose={() => setShowResetSuccessPopup(false)}
        heading={resetLinkHeading}
        email={resetEmail}
        autoCloseSeconds={5}
      />

      <OtpPopup isVisible={showPopup} onClose={() => setShowPopup(false)} status={popupStatus} message={popupMessage} errorTitle={popupErrorTitle} />
    </>
  )
}

export default InviteUser
